import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Check, X } from "lucide-react";

/**
 * Upload a file and receive back an API-relative URL. onUploaded(url, meta).
 */
export default function FileUploader({ onUploaded, label = "Upload file", accept = "*", testid = "file-uploader" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState(null);

  const submit = async (file) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backend = process.env.REACT_APP_BACKEND_URL;
      const absolute = `${backend}${data.url}`;
      setUploaded({ ...data, absolute });
      // Store the relative URL — it resolves on any domain (preview & production).
      onUploaded(data.url, data);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="border hairline p-3 bg-[color:var(--brand-surface2)]/40">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={()=>ref.current?.click()}
          data-testid={testid}
          disabled={busy}
          className="btn-ghost text-xs flex items-center gap-2"
        >
          <Upload size={12}/> {busy ? "Uploading…" : label}
        </button>
        {uploaded && (
          <>
            <Check size={14} className="text-emerald-700"/>
            <a href={uploaded.absolute} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--brand-primary)] link-underline truncate max-w-[16rem]">
              {uploaded.filename}
            </a>
            <button onClick={()=>{setUploaded(null); onUploaded("", null);}} type="button" className="text-neutral-500 hover:text-black"><X size={12}/></button>
          </>
        )}
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={e => e.target.files?.[0] && submit(e.target.files[0])}/>
    </div>
  );
}
